// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { coverThumbs, IMAGE_SIZES } from 'lib/community/discussions'
import type { NoteImage } from 'lib/dealhunt/types'
import { noteImageCount, noteImages, photoAlt } from 'lib/owner-notes/review-photos'
import { lightboxImages } from './review-photo-set'
import styles from './ReviewPhotos.module.css'

// Rendered only once a photo is pressed: never in the server HTML, and its stylesheet is not
// loaded by a page (the product page) until then.
const Lightbox = dynamic(() => import('components/community/discussions/Lightbox'), {
  ssr: false,
})

interface ReviewPhotosProps {
  /** Any owner-note object DealHunt sends (`images` absent = no photos, nothing drawn). */
  note: { images?: NoteImage[]; image_count?: number }
  /** For the alt text a photo without its own falls back to ("<product> — owner photo"). */
  productName: string | null
  /** `strip` (default): three ≤ 120 px 4:3 thumbs — the product page wall, Gear, the author's own
   *  card. `large` (the feed row, 2026-10-19): one 220 px-tall row, each photo at its own ratio
   *  (3:4 to 16:9, no crop for a phone portrait), sideways scroll when they outrun the row. */
  size?: 'strip' | 'large'
  className?: string
}

/** A photo's own box: its ratio between 3:4 (a phone portrait) and 16:9; 4:3 without dimensions. */
export function photoAspectRatio(width: number, height: number): string {
  if (!(width > 0) || !(height > 0)) {
    return '4 / 3'
  }
  const ratio = Math.min(16 / 9, Math.max(3 / 4, width / height))
  return `${Math.round(ratio * 1000)} / 1000`
}

/**
 * An owner review's public photos as a strip (contract-review-photos §3): up to three 4:3 thumbs,
 * "+N" on the last — the feed, the product page wall, Gear, the author's own card. Each opens the
 * lightbox on the whole set. Explicit 4:3 boxes so nothing moves when a photo loads; `srcSet`
 * from DealHunt's `thumb` / `card` sizes when sent (`imageSource`), the plain `url` otherwise;
 * always lazy (a wall renders many). The thread draws the mosaic instead (`ReviewPhotoMosaic`).
 * No per-photo report: a reader reports the whole review.
 */
const ReviewPhotos: React.VFC<ReviewPhotosProps> = ({
  note,
  productName,
  size = 'strip',
  className,
}) => {
  const [open, setOpen] = useState<number | null>(null)
  const images = noteImages(note)
  if (!images.length) {
    return null
  }
  const set = lightboxImages(images, photoAlt({}, productName))
  const lightbox = open !== null && (
    <Lightbox images={set} start={open} onClose={() => setOpen(null)} />
  )
  const thumbs = coverThumbs({ cover_images: images, image_count: noteImageCount(note) })
  const large = size === 'large'
  const sizes = large ? IMAGE_SIZES.photoLarge : IMAGE_SIZES.cardThumb
  return (
    <div className={className}>
      <div className={large ? styles.stripLarge : styles.strip}>
        {thumbs.map((thumb, i) => (
          <button
            // eslint-disable-next-line react/no-array-index-key -- the same photo may repeat
            key={`${thumb.src}-${i}`}
            type="button"
            className={styles.thumb}
            style={large ? { aspectRatio: photoAspectRatio(thumb.width, thumb.height) } : undefined}
            aria-label={`Open photo ${i + 1} of ${images.length}: ${set[i]?.alt ?? ''}`}
            onClick={() => setOpen(i)}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={thumb.src}
              srcSet={thumb.srcSet}
              sizes={thumb.srcSet ? sizes : undefined}
              alt=""
              width={thumb.width || undefined}
              height={thumb.height || undefined}
              loading="lazy"
              decoding="async"
            />
            {thumb.more > 0 && <span className={styles.more}>{`+${thumb.more}`}</span>}
          </button>
        ))}
      </div>
      {lightbox}
    </div>
  )
}

export default ReviewPhotos
